import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Typography,
  Box,
} from '@mui/material';
import axios from '../../api/client';
import { format } from 'date-fns';
import { APPROVAL_LABELS } from './ApprovalChip';

const ACTION_LABELS = {
  submit: 'Submitted',
  approve: 'Approved',
  query: 'Queried',
  reject: 'Rejected',
  issue: 'Funds Released',
};

const ACTION_COLORS = {
  submit: 'info',
  approve: 'success',
  query: 'warning',
  reject: 'error',
  issue: 'primary',
};

const STAGE_LABELS = {
  creation: 'Creation',
  review: 'Review',
  final: 'Final Approval',
  issuance: 'Issuance',
};

/**
 * Read-only audit trail for any resource using the shared approval workflow.
 * Fetches on mount rather than taking rows as a prop, so it always shows the latest state.
 *
 * @param resourcePath API segment, e.g. 'repairs' or 'requisitions'
 * @param itemId       the record id
 * @param emptyMessage what to show when nothing has happened yet
 */
const ApprovalHistory = ({ resourcePath, itemId, emptyMessage }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchLogs = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/${resourcePath}/${itemId}/approval-log`);
        if (!cancelled) setLogs(response.data || []);
      } catch (error) {
        if (!cancelled) setLogs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (itemId) fetchLogs();
    // Guards against a late response from a previous item overwriting the current one.
    return () => { cancelled = true; };
  }, [resourcePath, itemId]);

  if (loading) {
    return <Typography variant="body2" color="text.secondary">Loading history...</Typography>;
  }

  if (logs.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyMessage || 'No approval activity yet — this is still a draft.'}
      </Typography>
    );
  }

  return (
    <Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>By</TableCell>
              <TableCell>Result</TableCell>
              <TableCell>Comment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log._id}>
                <TableCell>
                  {log.createdAt ? format(new Date(log.createdAt), 'dd MMM yyyy HH:mm') : '-'}
                </TableCell>
                <TableCell>{STAGE_LABELS[log.stage] || log.stage}</TableCell>
                <TableCell>
                  <Chip
                    label={ACTION_LABELS[log.action] || log.action}
                    color={ACTION_COLORS[log.action] || 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{log.actedBy?.name || '-'}</TableCell>
                {/* Issuance rows carry no status change — the approval status stays 'approved'. */}
                <TableCell>{log.toStatus ? (APPROVAL_LABELS[log.toStatus] || log.toStatus) : '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{log.comment || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ApprovalHistory;
